import z, { ZodType } from "zod/v4";
import { BooleanSchema } from "../ea.schema.helpers";

const RangeSchema = (schema: ZodType) =>
  z.object({
    gte: schema.optional(),
    lte: schema.optional(),
  });

export const Ea4DiscoveryEnabledResponseSchema = z.object({ enabled: z.boolean() });

// --- Discovery environments ---
export const Ea4DiscoveryEnvironmentResponseSchema = z.object({
  environments: z.array(z.string()),
  count: z.number(),
});

// --- Discovery patterns ---
export const Ea4DiscoveryGetPatternsRequestSchema = z.object({
  environment: z.string(),
  pageSize: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
});

export const Ea4DiscoveryPatternSchema = z.object({
  patternNameId: z.coerce.number(),
  patternName: z.string(),
  executions: z.coerce.number(),
});

export const Ea4DiscoveryGetPatternsResponseSchema = z.object({
  items: z.array(Ea4DiscoveryPatternSchema),
  count: z.number(),
  totalPatterns: z.number(),
});

// --- Discovery executions ---
export const Ea4DiscoveryGetExecutionsRequestSchema = z.object({
  patternNameId: z.coerce.number().optional(),
  environment: z.coerce.string(),
  where: z
    .looseObject({
      isRepresentative: BooleanSchema.optional(),
      startTime: RangeSchema(z.coerce.date()).optional(),
      duration: RangeSchema(z.coerce.number()).optional(),
      representativenessScore: RangeSchema(z.coerce.number()).optional(),
      stepCount: RangeSchema(z.coerce.number()).optional(),
    })
    .catchall(z.any())
    .optional(),
  orderBy: z.any().optional(),
  pageSize: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
});

export const Ea4DiscoveryExecutionSchema = z.object({
  id: z.string(),
  isRepresentative: z.boolean(),
  representativenessScore: z.coerce.number().optional(),
  duration: z.coerce.number(),
  stepCount: z.coerce.number(),
  sessionId: z.string(),
  cardSessionId: z.string(),
  streamUser: z.string(),
  patternName: z.string(),
  patternNameId: z.coerce.number(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
});

export const Ea4DiscoveryGetExecutionsResponseSchema = z.object({
  items: z.array(Ea4DiscoveryExecutionSchema),
  count: z.number(),
});

// --- Import script ---
export const Ea4DiscoveryImportEventsRequestSchema = z.object({
  projectId: z.coerce.number(),
  environment: z.string(),
  executionsId: z.array(z.string()).optional(),
  sessionsId: z.array(z.string()).optional(),
});

// --- Discovery events ---
export const Ea4DiscoveryEventSchema = z.object({
  id: z.string(),
  cardSessionId: z.string(),
  time: z.coerce.date(),
  message: z.object({
    step: z.object({
      label: z.string(),
      labelKey: z.string().nullable(),
      commandId: z.string().nullable(),
      paths: z.transform((data) => JSON.parse(data as string)),
      platform: z.object({
        id: z.string(),
        version: z.string().optional(),
      }),
      resolvers: z.any(),
      value: z.any(),
      rule: z.any(),
      structureVersion: z.string().nullable().optional(),
    }),
  }),
});

export const Ea4DiscoveryImportEventsResponseSchema = z.object({
  hits: z.array(Ea4DiscoveryEventSchema),
  num_hits: z.number(),
});

// --- Discovery sessions ---
export const Ea4DiscoverySessionSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
  streamUser: z.string(),
  realDuration: z.coerce.number(),
  duration: z.coerce.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  eTag: z.string(),
  userId: z.string(),
  eventCount: z.coerce.number(),
  lastUpdateTime: z.coerce.date(),
});

export const Ea4DiscoveryGetSessionsRequestSchema = z.object({
  environments: z.array(z.string().optional()),
  where: z
    .looseObject({
      duration: RangeSchema(z.coerce.number()).optional(),
      eventCount: RangeSchema(z.coerce.number()).optional(),
    })
    .catchall(z.any())
    .optional(),
  orderBy: z.any().optional(),
  pageSize: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
});

export const Ea4DiscoveryGetSessionsResponseSchema = z.object({
  items: z.array(Ea4DiscoverySessionSchema),
  count: z.number(),
  totalCount: z.number(),
});

// --- StreamUsers ---
export const Ea4DiscoveryGetStreamUsersRequestSchema = z.object({
  pageSize: z.coerce.number().default(50),
  continuationToken: z.string().optional(),
});

export const Ea4DiscoveryGetStreamUsersResponseSchema = z.object({
  items: z.array(
    z.object({
      Alias: z.string(),
      Name: z.string(),
      UserGuid: z.string(),
      LastActivityTime: z.coerce.date(),
      LastEnvironment: z.string(),
    }),
  ),
  continuationToken: z.string().optional(),
  hasMore: z.coerce.boolean(),
});
